import { getEndPoint } from "src/utils/endpoint";


let cache = {};
window['cache'] = cache;

/**
 * Formats the data for the request.
 * @param {Object|FormData} data - The data to format.
 * @returns {string|FormData} - The formatted data.
 */
function formatData(data) {
  if (data instanceof FormData) {
    // Don't format FormData, just return as is
    return data;
  }
  if (window['config'].ENCODE_REQUESTS === 'true') {
    return JSON.stringify({
      data: Buffer.from(JSON.stringify(data)).toString('base64'),
    });
  }
  return JSON.stringify(data);
}

/**
 * Sends a JSON request or FormData to the server and handles the response.
 * @param {"GET"|"POST"|"DELETE"|"PUT"} method - The HTTP method.
 * @param {string} path - The endpoint path.
 * @param {Object|FormData} [data] - The request payload.
 * @param {boolean} [useLocalCache] - Whether to use local cache for JSON responses.
 * @param {function} [showAlert] - Optional alert function to show errors.
 * @param {boolean} [returnRaw] - Whether to return the raw response instead of parsing as JSON.
 * @returns {Promise<Object|Response|null>} - The JSON response, raw Response (if returnRaw=true), or null if an error occurred.
 */
export async function requestJSON(method, path, data, useLocalCache, showAlert, returnRaw = false) {
  const shouldCache = typeof useLocalCache === 'boolean' && useLocalCache;
  
  const cacheKey = JSON.stringify({ method, path, data });
  if (shouldCache && cache[cacheKey]) {
    return cache[cacheKey];
  }

  const endpoint = getEndPoint();
  try {
    let headers = {};
    let body = undefined;

    if (data instanceof FormData) {
      body = data;
      // Do not set Content-Type header for FormData; browser will set it including boundary
    } else if (data) {
      headers["Content-Type"] = "application/json";
      headers["Content-Transfer-Encoding"] = window['config'].ENCODE_REQUESTS === 'true' ? "base64" : "";
      body = formatData(data);
    }

    const response = await fetch(endpoint + path, {
      method,
      headers,
      body,
      credentials: "include",
    });

    // If we want the raw response (for file downloads, etc.), return it here
    if (returnRaw) {
      if (!response.ok && showAlert) {
        // Try to extract error message for the alert
        const errorData = await response.clone().json().catch(() => ({}));
        showAlert({
          message: errorData.message || errorData.error || `Error: ${response.status} ${response.statusText}`,
          severity: "error",
          title: "Error",
        });
        return null;
      }
      return response;
    }

    // Otherwise parse as JSON as before
    const jsonResponse = await response.json().catch(() => null);
    
    if (shouldCache) {
      cache[cacheKey] = jsonResponse;
    }

    if (!response.ok) {
      if (showAlert) {
        showAlert({
          message: jsonResponse ? jsonResponse.message || jsonResponse.error || "An error occurred" : "An error occurred",
          severity: "error",
          title: "Error",
        });
      }
      return null;
    }

    return jsonResponse;
  } catch (error) {
    console.error("Network error:", error);
    if (showAlert) {
      showAlert({
        message: error.message || "An unexpected error occurred",
        severity: "error",
        title: "Error",
      });
    }
    return null;
  }
}

/**
 * Invalidates the cache for a specific request.
 * @param {"GET"|"POST"|"DELETE"|"PUT"} method - The HTTP method.
 * @param {string} path - The endpoint path.
 * @param {Object} data - The request payload.
 */
export function invalidateCache(method, path, data) {
  const cacheKey = JSON.stringify({ method, path, data });
  delete cache[cacheKey];
}
